import { infoToast } from "@/components/ui/toast.tsx";
import { clearClientState } from "@/lib/clearClientState.ts";
import { stashToast } from "@/lib/deferredToast.ts";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { parseOnboardingThreadId } from "@/shared/onboardingLanding.ts";
import { useEffect } from "react";

/**
 * Result payload sent by OAuthResult.tsx via BroadcastChannel("oauth_result") for
 * the add-account flow. Mirrors the URL query params the backend redirect sets.
 */
type AddAccountOAuthResult = {
  flow: string;
  success: boolean;
  accountId?: number;
  alreadyAdded?: boolean;
  sameAccount?: boolean;
  email?: string | null;
  error?: string | null;
  /**
   * `unknown`, not `number`: this crosses from a page whose params are whatever
   * a crafted link put in them, so the only way to a usable value is through
   * `parseOnboardingThreadId`. A `number` here would let a future edit read it
   * straight out with the compiler's blessing.
   */
  onboardingThreadId?: unknown;
};

export type AddAccountOAuthSuccess = Extract<AddAccountOAuthResult, { success: true }> & {
  success: true;
};

/**
 * Listens for add-account OAuth results broadcast from the popup tab.
 *
 * - Default success (new account): stash a "Account added — switching to {email}" toast and
 *   load the new account's setup thread, or plain-reload when the server sent no usable
 *   thread id. The backend has already re-issued the bba cookie, so either lands in the
 *   new account.
 * - A1 (already_added + same_account): fire a confirmation toast in the *current* session —
 *   no reload.
 * - A2 (already_added, different account): stash a "Already added — switching to {email}"
 *   toast and reload. Cookie is already re-issued to the existing account.
 * - Error (Case C / other): call `onError(errorCode)`.
 */
export const useAddAccountOAuthResult = ({ onError }: { onError: (errorCode: string) => void }) => {
  useEffect(() => {
    const channel = new BroadcastChannel("oauth_result");

    channel.onmessage = (event: MessageEvent<AddAccountOAuthResult>) => {
      if (event.data.flow !== "add_account") return;

      const { success, alreadyAdded, sameAccount, email, error, onboardingThreadId } = event.data;

      if (success) {
        if (alreadyAdded && sameAccount) {
          // A1: re-adding the current account. Fire a neutral info toast — nothing
          // was added, nothing changed, so "success" (green check) would be misleading.
          infoToast("This account is already connected.");
          return;
        }

        // Default success OR A2 (switch to existing account). Both reload after stashing
        // the toast so it survives.
        //
        // Toast kind differs: default is a real success (new account created → green check),
        // A2 is neutral info — the user's intent was to add, but nothing was added; they
        // just ended up switching to an existing account. Green check would misleadingly
        // suggest the add succeeded.
        const emailLabel = email ?? "this account";
        if (alreadyAdded) {
          stashToast({
            kind: "info",
            message: `Already added. Switching to ${emailLabel}.`,
          });
        } else {
          stashToast({
            kind: "success",
            message: `Account added! Switching to ${emailLabel}.`,
          });
        }
        // The reload lands in the other account: the same identity change as
        // a switch, so the same targeted reset keeps this account's
        // preferences and recent contacts from greeting it.
        clearClientState();

        // A newly added account arrives with its own setup thread, and landing
        // in it is the point — the cookie has already switched, so staying put
        // would drop the user on an account with nothing in it yet.
        //
        // Re-parsed rather than trusted. The broadcast crosses from a page
        // whose params are whatever a crafted link put in them, and validating
        // at both ends costs nothing (BIG-8170).
        //
        // Honoured on a re-add too, which is how an add whose response was lost
        // recovers: the server sends a pointer there only for a switch to
        // another of this user's accounts whose setup is still unfinished, so
        // an id arriving at all is one worth landing on.
        const threadId = parseOnboardingThreadId(onboardingThreadId);
        if (threadId !== undefined) {
          // Assigned rather than routed: this is the same full-document load
          // the reload below performs, which is what re-reads the new account's
          // cookie. A client-side route change would keep the old account's
          // module state.
          window.location.href = getThreadPagePath(threadId);
          return;
        }

        window.location.reload();
        return;
      }

      onError(error ?? "unknown_error");
    };

    return () => {
      channel.close();
    };
  }, [onError]);
};
